/**
 * Oracle SQL Error Parser — IDE-style diagnostics
 * Prefers structured `diagnostic` from the API; falls back to parsing raw text.
 */
import { analyzeSqlError, locateIdentifierInQuery } from './sqlErrorAnalyzer';

/** Prefix added when wrapping with ROWNUM: "SELECT * FROM (" */
export const ROWNUM_WRAP_PREFIX = 'SELECT * FROM (';
const WRAP_PREFIX_LENGTH = ROWNUM_WRAP_PREFIX.length;

const STACK_TRACE_CODES = ['ORA-06512', 'ORA-06508', 'ORA-04063', 'ORA-06504', 'ORA-06502'];

/**
 * Parse Oracle error message and extract structured information
 * @param {string} errorText - Raw error message from Oracle
 * @returns {Object} Parsed error information
 */
export function parseOracleError(errorText) {
  if (!errorText || typeof errorText !== 'string') {
    return { formatted: errorText || 'Unknown error', lineNumber: null, errorCode: null };
  }

  const result = {
    formatted: errorText,
    lineNumber: null,
    errorCode: null,
    position: null,
    message: errorText,
    suggestions: [],
  };

  const oraCodeMatch = errorText.match(/ORA-(\d{5})/i);
  if (oraCodeMatch) {
    result.errorCode = `ORA-${oraCodeMatch[1]}`;
  }

  let textForLineParse = errorText;
  for (const code of STACK_TRACE_CODES) {
    textForLineParse = textForLineParse
      .replace(new RegExp(`\\s*\\|\\s*${code}[^|]*`, 'gi'), '')
      .trim();
  }

  const lineColMatch = textForLineParse.match(/line\s+(\d+)[,:\s]+(?:column|position)\s+(\d+)/i);
  if (lineColMatch) {
    result.lineNumber = parseInt(lineColMatch[1], 10);
    result.position = parseInt(lineColMatch[2], 10);
  } else {
    const lineMatch = textForLineParse.match(/(?:at\s+)?line\s+(\d+)/i);
    if (lineMatch) {
      result.lineNumber = parseInt(lineMatch[1], 10);
    }
    const posMatch = textForLineParse.match(/(?:position|column)\s+(\d+)/i);
    if (posMatch) {
      result.position = parseInt(posMatch[1], 10);
    }
  }

  const oraMessageMatch = errorText.match(/ORA-\d+:\s*([^\n\r|]+)/i);
  if (oraMessageMatch) {
    result.message = oraMessageMatch[1].trim();
  }

  let formatted = '';
  if (result.errorCode) formatted += `[${result.errorCode}] `;
  formatted += result.message;
  if (result.lineNumber) formatted += `\nLine ${result.lineNumber}`;
  if (result.position) formatted += ` (Position ${result.position})`;
  result.formatted = formatted;
  return result;
}

/**
 * Map Oracle's character position (from wrapped query) to user's original query
 */
export function mapWrappedPositionToUser(wrappedPosition, userQuery) {
  if (wrappedPosition == null || !userQuery || wrappedPosition < 1) return null;
  const userStart = WRAP_PREFIX_LENGTH + 1;
  const userEnd = WRAP_PREFIX_LENGTH + userQuery.length;
  if (wrappedPosition >= userStart && wrappedPosition <= userEnd) {
    return wrappedPosition - WRAP_PREFIX_LENGTH;
  }
  return null;
}

function lineColFromPosition(query, position1Based) {
  if (!query || position1Based == null || position1Based < 1) return null;
  const before = query.substring(0, position1Based - 1);
  const lines = before.split(/\r?\n/);
  return {
    lineNumber: lines.length,
    column: (lines[lines.length - 1] || '').length + 1,
    content: (query.split(/\r?\n/)[lines.length - 1] || ''),
  };
}

/**
 * Build display model from API `diagnostic` object when present.
 */
function fromApiDiagnostic(diagnostic, query, context = {}) {
  const source = diagnostic.source || 'oracle';
  const sourceLabel =
    source === 'local' ? 'Local check' : source === 'catalog' ? 'Catalog' : 'Oracle';

  const errorCode = diagnostic.errorCode || null;
  const message = diagnostic.message || diagnostic.primaryError || 'Query failed';
  const objectName = diagnostic.objectName || null;

  let highlightedLine = null;
  let conf = diagnostic.rangeConfidence || 'none';
  if (diagnostic.range && (conf === 'high' || conf === 'mapped' || conf === 'medium') && query) {
    const lines = query.split(/\r?\n/);
    const ln = diagnostic.range.line;
    if (ln >= 1 && ln <= lines.length) {
      highlightedLine = {
        lineNumber: ln,
        content: lines[ln - 1],
        column: diagnostic.range.column || null,
      };
    }
  }

  const tables = context.tables || [];
  const columns = context.columns || [];
  let sqlAnalysis = null;
  try {
    sqlAnalysis = analyzeSqlError(errorCode, message, query, {
      tables,
      columns,
      objectName,
      objectType: diagnostic.objectType,
      fixes: diagnostic.fixes || [],
    });
  } catch (_) {
    sqlAnalysis = {
      plainExplanation: objectName
        ? `${diagnostic.objectType === 'column' ? 'Column' : 'Object'} "${objectName}" — ${message}`
        : message,
      didYouMean: null,
      typoSuggestion: null,
      fixes: diagnostic.fixes || [],
    };
  }

  // Prefer API fixes when analyzer has none
  if (sqlAnalysis && (!sqlAnalysis.fixes || sqlAnalysis.fixes.length === 0) && diagnostic.fixes?.length) {
    sqlAnalysis.fixes = diagnostic.fixes;
  }

  // Keyword / missing-connector diagnostics already have full detail from the API
  if (sqlAnalysis && diagnostic.objectType === 'keyword') {
    sqlAnalysis.plainExplanation = message;
    if (diagnostic.detail) sqlAnalysis.typoSuggestion = diagnostic.detail;
    if (diagnostic.fixes?.length) sqlAnalysis.fixes = diagnostic.fixes;
  }

  // If Oracle gave no trustworthy range, locate the named object in the user's SQL
  if (!highlightedLine && query && (objectName || sqlAnalysis?.highlightedPart)) {
    const loc =
      sqlAnalysis?.location ||
      locateIdentifierInQuery(query, objectName || sqlAnalysis.highlightedPart);
    if (loc) {
      highlightedLine = {
        lineNumber: loc.line,
        content: loc.content,
        column: loc.column,
      };
      conf = 'located';
    }
  }

  const displayMessage =
    diagnostic.objectType === 'keyword'
      ? message
      : (errorCode === 'ORA-00907' || errorCode === 'ORA-00918') &&
          sqlAnalysis?.plainExplanation &&
          (/comma or JOIN|ambiguous|Missing AND|Keyword typo/i.test(sqlAnalysis.plainExplanation))
        ? sqlAnalysis.plainExplanation
        : message;
  const formatted = errorCode ? `[${errorCode}] ${displayMessage}` : displayMessage;

  return {
    errorCode: errorCode || (source === 'local' ? 'SQL Syntax' : null),
    message: displayMessage,
    formatted,
    objectName: objectName || sqlAnalysis?.highlightedPart || null,
    objectType: diagnostic.objectType || null,
    source,
    sourceLabel,
    primaryError: diagnostic.primaryError || formatted,
    highlightedLine,
    rangeConfidence: conf,
    suggestions: sqlAnalysis?.fixes || diagnostic.fixes || [],
    hasSuggestions: (sqlAnalysis?.fixes || diagnostic.fixes || []).length > 0,
    sqlAnalysis,
  };
}

/**
 * Format error for display in the UI
 * @param {string} errorText - Raw error text
 * @param {string} query - The SQL query (user's original, unwrapped)
 * @param {Object} context - { tables, wrapped, diagnostic }
 */
export function formatErrorForDisplay(errorText, query = '', context = {}) {
  if (context.diagnostic && typeof context.diagnostic === 'object') {
    return fromApiDiagnostic(context.diagnostic, query, context);
  }

  const parsed = parseOracleError(errorText);
  const isPrevalidateError = /SQL syntax error|Local check:|Missing comma|Unclosed|Unmatched/i.test(
    errorText || ''
  );
  const source = isPrevalidateError ? 'local' : 'oracle';
  const sourceLabel = source === 'local' ? 'Local check' : 'Oracle';

  let sqlAnalysis = null;
  if (parsed.errorCode || isPrevalidateError) {
    try {
      sqlAnalysis = analyzeSqlError(parsed.errorCode || 'SQL Syntax', parsed.message, query, context);
    } catch (e) {
      /* ignore */
    }
  }

  let highlightedLine = null;
  if (query && isPrevalidateError && parsed.lineNumber && parsed.lineNumber > 0) {
    const lines = query.split(/\r?\n/);
    if (parsed.lineNumber <= lines.length) {
      highlightedLine = {
        lineNumber: parsed.lineNumber,
        content: lines[parsed.lineNumber - 1],
        column: parsed.position || null,
      };
    }
  } else if (query && parsed.position && context.wrapped) {
    const userPos = mapWrappedPositionToUser(parsed.position, query);
    const mapped = lineColFromPosition(query, userPos);
    if (mapped) highlightedLine = mapped;
  } else if (query && parsed.position && !context.wrapped) {
    const mapped = lineColFromPosition(query, parsed.position);
    if (mapped) highlightedLine = mapped;
  } else if (query && (sqlAnalysis?.location || sqlAnalysis?.highlightedPart)) {
    const loc =
      sqlAnalysis.location || locateIdentifierInQuery(query, sqlAnalysis.highlightedPart);
    if (loc) {
      highlightedLine = {
        lineNumber: loc.line,
        content: loc.content,
        column: loc.column,
      };
    }
  }
  // Do not highlight from bare line numbers on wrapped Oracle errors (unreliable).

  const fixes = sqlAnalysis?.fixes || [];
  const displayMessage =
    (parsed.errorCode === 'ORA-00907' || parsed.errorCode === 'ORA-00918') &&
    sqlAnalysis?.plainExplanation &&
    /comma or JOIN|ambiguous/i.test(sqlAnalysis.plainExplanation)
      ? sqlAnalysis.plainExplanation
      : parsed.message;

  return {
    ...parsed,
    message: displayMessage,
    formatted: parsed.errorCode ? `[${parsed.errorCode}] ${displayMessage}` : displayMessage,
    source,
    sourceLabel,
    objectName: sqlAnalysis?.highlightedPart || null,
    highlightedLine,
    sqlAnalysis,
    suggestions: fixes,
    hasSuggestions: fixes.length > 0,
  };
}
